// Iframe-upotusten esto ennen suostumusta (V2-T7).
//
// Upotus (YouTube, Vimeo, Google Maps, sosiaalisen median widgetit) lataa
// kolmannen osapuolen sisältöä ja asettaa evästeitä heti — se on
// seurantapyyntö siinä missä skriptitagi. Siksi ei-välttämättömien
// kategorioiden upotukset estetään samalla logiikalla kuin skriptit:
// src siirretään talteen data-cmp-original-src-attribuuttiin, elementti
// merkitään data-cmp-blocked-attribuutilla (skannerinäkyvyys, sama sopimus
// kuin skripteillä) ja kävijälle näytetään placeholder, joka kertoo mitä
// estettiin ja tarjoaa napin valinnan tekemiseen.
//
// HUOM asennustapa (PL-18): HTML:ään kirjoitettu <iframe src="..."> voi
// aloittaa latauksen ennen kuin mikään selainpuolen CMP pääsee väliin. Siksi
// suositeltu asennus on portitettu upotus:
//   <iframe data-cmp-src="https://www.youtube.com/embed/..."
//           data-cmp-category="marketing"></iframe>
// Tällöin selain ei lataa mitään ennen suostumusta. Tavallinen src-attribuutti
// neutralisoidaan parhaan yrityksen mukaan (lataus katkaistaan, sisältö
// piilotetaan, placeholder näytetään) — dynaamisesti luodut upotukset
// siepataan luotettavasti src:n asetushetkellä.

import type { BlockPatterns, Choices } from './types';

/** Estetty upotus odottamassa vapautusta. */
export interface BlockedIframe {
  node: HTMLIFrameElement;
  slug: string;
  src: string;
  placeholder: HTMLElement;
}

/**
 * Fallback-patternit upotuksille, kun konfiguraatiota ei ole vielä ladattu.
 * Slugit vastaavat skriptipatternien slugeja (patterns.ts). Konfiguraation
 * evästeiden tarjoajat täydentävät listaa välimuistin kautta.
 */
export const IFRAME_FALLBACK_PATTERNS: BlockPatterns = {
  analytics: ['hotjar.com', 'clarity.ms'],
  marketing: [
    'youtube.com/embed',
    'youtube-nocookie.com/embed',
    'youtu.be',
    'doubleclick.net',
    'googlesyndication.com',
  ],
  social: [
    'player.vimeo.com',
    'facebook.com/plugins',
    'platform.twitter.com',
    'instagram.com/p/',
    'linkedin.com/embed',
    'tiktok.com/embed',
  ],
  preferences: ['google.com/maps', 'maps.google.com', 'openstreetmap.org', 'crisp.chat'],
};

interface PlaceholderTexts {
  title: string;
  body: string;
  action: string;
}

/**
 * Placeholderin tekstit. Kieli luetaan sivun lang-attribuutista; tuntematon
 * kieli putoaa englantiin. Tekstit ovat blokkerissa sisäänrakennettuina,
 * koska placeholder näytetään ennen kuin konfiguraatio (ja sen tekstit) on
 * ladattu verkosta.
 */
export function placeholderTexts(lang: string | null | undefined): PlaceholderTexts {
  const l = (lang || '').substring(0, 2).toLowerCase();
  if (l === 'fi') {
    return {
      title: 'Sisältö on estetty',
      body: 'Tämä upotus lataa sisältöä kolmannelta osapuolelta. Se näytetään, kun hyväksyt kyseisen evästekategorian.',
      action: 'Salli sisältö',
    };
  }
  if (l === 'sv') {
    return {
      title: 'Innehållet är blockerat',
      body: 'Den här inbäddningen laddar innehåll från en tredje part. Det visas när du godkänner motsvarande cookiekategori.',
      action: 'Tillåt innehåll',
    };
  }
  return {
    title: 'Content blocked',
    body: 'This embed loads content from a third party. It is shown once you accept the corresponding cookie category.',
    action: 'Allow content',
  };
}

/** Upotuksen kohdehost näytettäväksi (tai koko src, jos jäsennys ei onnistu). */
function hostOf(src: string): string {
  const m = src.match(/^[a-z]+:\/\/([^/?#]+)/i);
  return m ? m[1] : src;
}

/** Placeholder-elementti estetyn upotuksen tilalle. */
export function createPlaceholder(slug: string, src: string, lang: string | null | undefined): HTMLElement {
  const t = placeholderTexts(lang);
  const box = document.createElement('div');
  box.setAttribute('data-cmp-placeholder', slug);
  box.setAttribute('role', 'group');
  box.setAttribute('aria-label', t.title);
  box.style.cssText = [
    'display:flex', 'flex-direction:column', 'align-items:center', 'justify-content:center',
    'gap:8px', 'padding:16px', 'min-height:120px', 'box-sizing:border-box',
    'border:1px solid #d1d5db', 'border-radius:8px', 'background:#f9fafb', 'color:#1f2937',
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    'font-size:13px', 'text-align:center',
  ].join(';');

  const title = document.createElement('strong');
  title.textContent = t.title;
  title.style.fontSize = '14px';
  box.appendChild(title);

  const body = document.createElement('span');
  body.textContent = t.body;
  body.style.cssText = 'max-width:420px;line-height:1.4;opacity:0.8';
  box.appendChild(body);

  // Mitä estettiin: kategoria ja kohdehost kävijän nähtäville (läpinäkyvyys)
  const detail = document.createElement('span');
  detail.style.cssText = 'font-size:11px;opacity:0.6;word-break:break-all';
  detail.textContent = hostOf(src) + ' \u2013 ' + slug;
  box.appendChild(detail);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = t.action;
  btn.setAttribute('data-cmp-placeholder-action', slug);
  btn.style.cssText = [
    'padding:8px 16px', 'border:none', 'border-radius:6px', 'cursor:pointer',
    'font-weight:600', 'font-size:13px', 'background:#2563eb', 'color:#fff',
  ].join(';');
  btn.onclick = function () {
    if (typeof window.CMP_showBanner === 'function') window.CMP_showBanner();
  };
  box.appendChild(btn);

  return box;
}

/**
 * Estää upotuksen: siirtää src:n talteen, merkitsee elementin
 * skannerinäkyvästi, piilottaa sen ja lisää placeholderin tilalle.
 */
export function blockIframe(
  el: HTMLIFrameElement,
  slug: string,
  src: string,
  lang: string | null | undefined,
): BlockedIframe {
  el.setAttribute('data-cmp-blocked', slug);
  el.setAttribute('data-cmp-original-src', src);
  el.removeAttribute('src');
  el.removeAttribute('data-cmp-src');
  // about:blank estää latauksen myös silloin, kun selain on jo aloittanut
  // navigoinnin attribuutin poiston jälkeen
  try { el.contentWindow?.location.replace('about:blank'); } catch { /* cross-origin */ }
  el.style.display = 'none';

  const placeholder = createPlaceholder(slug, src, lang);
  const entry: BlockedIframe = { node: el, slug, src, placeholder };
  attachPlaceholder(entry);
  return entry;
}

/**
 * Lisää placeholderin estetyn upotuksen viereen. Dynaamisesti luodulla
 * upotuksella src asetetaan usein ennen kuin elementti on liitetty sivulle,
 * joten placeholder lisätään vasta liitoksen jälkeen (blokkerin observer
 * kutsuu tätä uudelleen).
 */
export function attachPlaceholder(entry: BlockedIframe): void {
  if (entry.placeholder.parentNode) return;
  if (entry.node.parentNode) entry.node.parentNode.insertBefore(entry.placeholder, entry.node);
}

/** Vapauttaa estetyn upotuksen: poistaa placeholderin ja palauttaa src:n. */
export function releaseIframe(entry: BlockedIframe): void {
  if (entry.placeholder.parentNode) entry.placeholder.parentNode.removeChild(entry.placeholder);
  entry.node.style.display = '';
  entry.node.removeAttribute('data-cmp-blocked');
  entry.node.removeAttribute('data-cmp-original-src');
  entry.node.setAttribute('src', entry.src);
}

/**
 * Vapautetaanko tämän kategorian upotus annetuilla valinnoilla.
 *
 * Erikoistapaus: jos estetyn upotuksen kategoriaa ei ole sivuston
 * konfiguraatiossa lainkaan (fallback-patternin slug, jota asiakas ei ole
 * ottanut käyttöön), upotus vapautetaan vain jos kävijä hyväksyi kaikki
 * ei-välttämättömät kategoriansa. Muuten placeholder jäisi pysyvästi
 * paikalleen, koska kävijä ei voi hyväksyä kategoriaa jota ei ole.
 */
export function shouldReleaseIframe(slug: string, choices: Choices): boolean {
  if (Object.prototype.hasOwnProperty.call(choices, slug)) return !!choices[slug];

  const others = Object.keys(choices).filter(function (s) {
    return s !== 'essential' && s !== 'valttamattomat';
  });
  return others.length > 0 && others.every(function (s) { return !!choices[s]; });
}
